<template>
  <a-modal v-model:open="open" width="1000px"
           class="car-select-modal"
           footer=""
           :closable="false"
           :mask-closable="true"
           @cancel="handleCancelClose">
    <template #title>
      <div class="flex aic jcsb">
        <div class="modal_title">{{ modalTitle }}</div>
        <div class="modal-header-actions">
          <div class="out" @click="handleCancelClose">
            <CloseOutlined />
          </div>
        </div>
      </div>
    </template>
    <div class="car-modal-content">
      <!-- 搜索区域 -->
      <div class="query-item">
        <div class="search-filters">
          <div>
            测试牌照
            <a-input class="inp ml24" placeholder="请输入" v-model:value="query.testCarNum"></a-input>
          </div>
          <div class="ml30">
            车架号
            <a-input class="inp ml24" placeholder="请输入" v-model:value="query.frameNum"></a-input>
          </div>

          <a-button class="ml30" @click="search" type="primary" ghost>查询</a-button>
          <a-button class="ml24" @click="reset" type="primary" ghost>重置</a-button>
        </div>
      </div>

      <!-- 表格 -->
      <a-table
          :columns="carColumns"
          :data-source="carList"
          :pagination="pagination"
          row-key="id"
          :loading="loading"
          size="middle"
          :scroll="{ x: 800, y: 'auto' }"
          class="mt23"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'testCarNum'">
            {{ record.testCarNum }}
            <div style="color:red;font-size:12px" v-if="record.plateExpireWarn">{{ record.plateExpireWarn }}</div>
          </template>
          <template v-if="column.key === 'carType'">
            {{ getSingleTypeValue(appStore.SysDictVOList, 'carInfo' + 'category' + record.carType) }}
          </template>
          <template v-if="column.key === 'carColor'">
            {{ getSingleTypeValue(appStore.SysDictVOList, 'carInfo' + 'fuselageColor' + record.carColor) }}
          </template>
          <template v-if="column.key === 'bindMonitorDevice'">
            {{ record.bindMonitorDevice == '0' ? '未安装' : '已安装' }}
          </template>
          <template v-if="column.key === 'action'">
            <a-button type="primary" size="small" @click="handleRowSelect(record)">选择</a-button>
          </template>
        </template>
      </a-table>
    </div>
  </a-modal>
</template>
<script setup>
import { ref, reactive, toRefs, onMounted, defineProps, defineEmits, defineExpose } from "vue";
import { useRouter } from "vue-router";
import { getCars } from "@/api/car/car.js";
import { CloseOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import useUserStore from '@/store/modules/user';
import useAppStore from "@/store/modules/app";
import { getSingleTypeValue } from "@/utils/matchDic.js";

const router = useRouter();
const store = useUserStore();
const appStore = useAppStore();

const props = defineProps({
  modalTitle: {
    type: String,
    default: () => "选择测试车辆"
  }
});

const emit = defineEmits(["selectedEvent", "cancelEvent"]);

const open = ref(false);
const loading = ref(false);
const carList = ref([]);

// 查询条件
const query = reactive({
  deptName: "",
  frameNum: "",
  testCarNum: "",
});

// 分页配置
const pagination = reactive({
  total: 0,
  size: "middle",
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  showQuickJumper: false,
  pageSizeOptions: ["10", "20", "30"],
  showTotal: (total) => `共有 ${total} 条数据`,
  onChange: pageChange,
  showSizeChange: pageSizeChange,
});

// 车辆表格列定义 - 移除序号列，添加操作列
const carColumns = [
  {
    title: "车架号",
    key: "frameNum",
    dataIndex: "frameNum",
    width: 180,
  },
  {
    title: "测试牌照",
    dataIndex: "testCarNum",
    key: "testCarNum",
    width: 140,
  },
  {
    title: "监管装置",
    key: "bindMonitorDevice",
    dataIndex: "bindMonitorDevice",
    width: 100,
  },
  {
    title: "车辆品牌",
    key: "carBrand",
    dataIndex: "carBrand",
    width: 120,
  },
  {
    title: "车辆型号",
    key: "carModel",
    dataIndex: "carModel",
    width: 120,
  },
  {
    title: "车辆类型",
    key: "carType",
    dataIndex: "carType",
    width: 100,
  },
  {
    title: "自动驾驶级别",
    key: "dynamicDriverLevel",
    dataIndex: "dynamicDriverLevel",
    width: 120,
  },
  {
    title: "车辆颜色",
    key: "carColor",
    dataIndex: "carColor",
    width: 100,
  },
  {
    title: "操作",
    key: "action",
    fixed: 'right',
    width: 100,
  }
];

// 分页变化处理
function pageChange(page, pageSizeVal) {
  pagination.current = page;
  getCarList();
}

function pageSizeChange(current, size) {
  pagination.pageSize = size;
  getCarList();
}

// 搜索
const search = () => {
  pagination.current = 1;
  getCarList();
};

// 重置
const reset = () => {
  query.deptName = "";
  query.frameNum = "";
  query.testCarNum = "";
  pagination.current = 1;
  getCarList();
};

// 行内选择方法
const handleRowSelect = (record) => {
  open.value = false;
  emit('selectedEvent', record);
};

// 显示模态框
const showModal = () => {
  open.value = true;
  pagination.current = 1;
  getCarList();
};

// 隐藏模态框
const hideModal = () => {
  open.value = false;
};

// 获取车辆列表
const getCarList = () => {
  loading.value = true;

  // 构建请求参数
  const params = {
    pageIndex: pagination.current,
    pageSize: pagination.pageSize,
    conditionMap: {
      ...query,
      deptIdStr: store.deptId  // 添加部门ID筛选
    }
  };

  getCars(params).then((res) => {
    if (res.code === 200) {
      carList.value = res.data.records || res.data;
      pagination.total = res.data.total || carList.value.length;
    } else {
      message.error(res.msg || '获取车辆列表失败');
      carList.value = [];
      pagination.total = 0;
    }
  }).finally(() => {
    loading.value = false;
  });
};

// 关闭模态框
const handleCancelClose = () => {
  open.value = false;
  emit('cancelEvent');
};

// 新增车辆
const handleAddCar = () => {
  open.value = false;
  router.push('/car/add');
};

// 暴露方法
defineExpose({
  showModal,
  hideModal,
  open
});
</script>
<style lang="less" scoped>
.modal_title {
  font-size: 18px;
  font-family: PingFang SC, PingFang SC;
  font-weight: 500;
  color: rgba(24, 24, 24, 1);
  line-height: 25px;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.out {
  cursor: pointer;
}

.car-modal-content {
  padding: 10px 0;
}

.query-item {
  margin-bottom: 16px;
}

.search-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.search-filters > div {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.inp {
  width: 180px;
}

.mt23 {
  margin-top: 23px;
}

.ml24 {
  margin-left: 24px;
}

.ml30 {
  margin-left: 30px;
}

:deep(.car-select-modal .ant-modal-wrap) {
  pointer-events: auto !important;
  position: fixed !important;
}

:deep(.car-select-modal .ant-modal-mask) {
  pointer-events: auto !important;
}

:deep(.car-select-modal) {
  pointer-events: none;
}

:deep(.car-select-modal .ant-modal-content) {
  pointer-events: auto;
}
</style>